import { useCallback, useRef, useState } from "react";
import { format, parseISO, addDays, subDays } from "date-fns";
import type { TripStore } from "../hooks/useTripData";
import type { TravelerId } from "../types";
import { CategoryIcon } from "./StatusBadge";
import { TodoList } from "./TodoList";
import { PlaceDetail } from "./PlaceDetail";
import { pinMayHavePhoto } from "../lib/googlePlaces";
import { SortableSchedule } from "./SortableSchedule";
import { DayMap } from "./DayMap";

interface Props {
  compact?: boolean;
  showMap?: boolean;
  date: string;
  store: TripStore;
  onSelectDate: (date: string) => void;
  voterId: TravelerId;
  selectedItemId?: string | null;
  onSelectItem?: (id: string) => void;
}

export function DayPanel({
  date,
  store,
  onSelectDate,
  voterId,
  compact = false,
  showMap = true,
  selectedItemId = null,
  onSelectItem,
}: Props) {
  const day = store.getDay(date);
  const travelers = store.data.meta.travelers;
  const dayPins = store.data.pins.filter((p) => p.dayIds?.includes(date));
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const [mapFocusKey, setMapFocusKey] = useState("");

  const prev = format(subDays(parseISO(date), 1), "yyyy-MM-dd");
  const next = format(addDays(parseISO(date), 1), "yyyy-MM-dd");
  const hasPrev = !!store.getDay(prev);
  const hasNext = !!store.getDay(next);

  const scrollToDayMap = useCallback(() => {
    // Stay on Itinerary — scroll to the compact day map (do not switch Map tab).
    requestAnimationFrame(() => {
      mapSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }, []);

  const handleSelectItem = useCallback(
    (id: string) => {
      onSelectItem?.(id);
      const key = `${id}:${Date.now()}`;
      setMapFocusKey(key);
      if (showMap) scrollToDayMap();
    },
    [onSelectItem, scrollToDayMap, showMap],
  );

  const mapJump =
    showMap && dayPins.length > 0 ? (
      <button
        type="button"
        className="day-map-jump"
        onClick={scrollToDayMap}
      >
        Map ↓
      </button>
    ) : null;

  if (!day) {
    return (
      <div className="card day-panel">
        <p className="muted">
          No plan for {format(parseISO(date), "MMM d, yyyy")}.
        </p>
      </div>
    );
  }

  return (
    <div className="day-panel-stack">
      {!compact && (
        <nav className="day-nav" aria-label="Day navigation">
          <button
            type="button"
            className="btn ghost sm"
            disabled={!hasPrev}
            onClick={() => hasPrev && onSelectDate(prev)}
          >
            ← Prev
          </button>
          <span className="day-nav-label">
            {format(parseISO(date), "EEE · MMM d")}
          </span>
          <button
            type="button"
            className="btn ghost sm"
            disabled={!hasNext}
            onClick={() => hasNext && onSelectDate(next)}
          >
            Next →
          </button>
        </nav>
      )}

      {!compact && (
        <header className="card day-hero">
          <p className="eyebrow">{format(parseISO(date), "EEEE")}</p>
          <h2 className="day-hero-heading">
            {format(parseISO(date), "MMMM d, yyyy")}
          </h2>
          <h3 className="day-title">{day.title}</h3>
          {mapJump}
          {day.location && <p className="day-loc">{day.location}</p>}
        </header>
      )}

      {compact && mapJump}

      <section className="card schedule-panel">
        <header className="panel-header">
          <h3>Schedule</h3>
          <span className="muted sm-hint schedule-hint">
            Morning → evening · drag food, coffee, attractions
          </span>
        </header>
        <SortableSchedule
          date={date}
          items={day.items}
          store={store}
          travelers={travelers}
          allPins={store.data.pins}
          dayPins={dayPins}
          voterId={voterId}
          selectedItemId={selectedItemId}
          onSelectItem={handleSelectItem}
        />
      </section>

      {showMap && dayPins.length > 0 && (
        <div ref={mapSectionRef} className="day-map-anchor">
          <DayMap
            data={store.data}
            date={date}
            selectedItemId={selectedItemId}
            focusKey={mapFocusKey}
          />
        </div>
      )}

      <TodoList
        key={date}
        todos={day.todos}
        onToggle={(id) => store.toggleTodo(date, id)}
        onAdd={(text) => store.addTodo(date, text)}
        onRemove={(id) => store.removeTodo(date, id)}
        onEdit={(id, text) => store.editTodo(date, id, text)}
      />

      {!compact && dayPins.length > 0 && (
        <section className="card">
          <header className="panel-header">
            <h3>Map pins this day</h3>
            <span className="muted">{dayPins.length}</span>
          </header>
          <ul className="pin-mini-list">
            {dayPins.map((p) => (
              <li
                key={p.id}
                className={[
                  pinMayHavePhoto(p) ? "has-photo" : "",
                  p.category === "coffee" ? "is-coffee" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined}
              >
                <CategoryIcon category={p.category} />
                <div>
                  <strong>{p.name}</strong>
                  {pinMayHavePhoto(p) && <PlaceDetail pin={p} compact />}
                  {p.notes && <p className="muted">{p.notes}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

